import type { FormSection } from './templateTypes';
import type { FieldValue } from './values';

/**
 * §1 Repeatable Sections — value-map key helpers.
 *
 * The runtime keeps a flat `Record<string, FieldValue>` for all advisor
 * values. Fields inside a repeatable section instance live under
 * `inst:<sectionId>:<index>:<fieldId>` so existing walkers (evaluator,
 * step-completion, uniqueness, sum) keep working unchanged — they just
 * see extra keys. Non-repeatable fields keep using the bare `<fieldId>`
 * key as before.
 *
 * The instance count for a repeatable section lives at
 * `__instanceCount:<sectionId>`. A fresh workflow renders one starter instance
 * without any bootstrap value, so {@link getInstanceCount} answers the
 * section's floor (default 1) when the map holds neither the sentinel nor an
 * answer. The sentinel is BROWSER-ONLY bookkeeping: `buildApplicationSnapshot`
 * does not persist it, so a map arriving from storage or from the submit
 * endpoint has the `inst:` keys and nothing else — which is why the count is
 * derived from those keys rather than assumed. See
 * {@link instanceCountFromValues}.
 */

/** Compose the instance-qualified value key for a field inside a
 *  repeatable section instance. */
export function qualifiedKey(sectionId: string, instanceIdx: number, fieldId: string): string {
  return `inst:${sectionId}:${instanceIdx}:${fieldId}`;
}

/** Composes the sentinel key that stores the current instance count for
 *  a repeatable section. */
export function instanceCountKey(sectionId: string): string {
  return `__instanceCount:${sectionId}`;
}

/**
 * The advisor-side floor for a section, defaulting to 1 for a section that
 * doesn't carry one. ALWAYS prefer this over a bare `1` when a section object
 * is in hand: `getInstanceCount` clamps to whatever floor it is given, so
 * passing the wrong one silently invents or hides instances. A floor of 0 is
 * legal and means "optional block" — nothing renders until the advisor adds.
 */
export function sectionMinInstances(section: FormSection): number {
  const min = section.minInstances;
  return typeof min === 'number' && Number.isFinite(min) && min >= 0 ? Math.floor(min) : 1;
}

/**
 * How many instances a repeatable section holds, read from the values map alone
 * and with no floor applied — 0 when the section has neither a sentinel nor a
 * single answer.
 *
 * Prefers the `__instanceCount:<sectionId>` sentinel the renderer maintains, and
 * falls back to the highest instance index any of the section's `inst:` keys
 * mentions, so a section populated WITHOUT a sentinel still reports honestly.
 *
 * **The fallback is not a nicety — it is the only correct reading on the server.**
 * `buildApplicationSnapshot` deliberately does not persist the sentinel (it is our
 * bookkeeping, not something BMO stores), so every map that reaches `PdfFunction`
 * at submit time carries `inst:` keys and no sentinel. Answering `min` there
 * reported one instance for a two-holder application, which silently dropped
 * Accountholder 2's AcroForm fills AND — via a `count_eq 2` role condition that
 * could then never pass — their e-signature anchors, producing a package where
 * OneSpan was told to find a tag that was never stamped.
 *
 * Safe against a REMOVED instance leaving stale keys behind, because the two
 * callers that can see such keys never reach the fallback: the renderer always
 * has the sentinel, and the snapshot writer bounds its own walk by the live
 * count, so the keys it emits never outrun it.
 */
export function instanceCountFromValues(
  values: Record<string, FieldValue>,
  sectionId: string,
): number {
  const raw = values[instanceCountKey(sectionId)];
  if (typeof raw === 'number' && Number.isFinite(raw)) return Math.max(0, Math.floor(raw));

  const prefix = `inst:${sectionId}:`;
  let maxIdx = -1;
  for (const key of Object.keys(values)) {
    if (!key.startsWith(prefix)) continue;
    const parts = key.split(':');
    // inst : sectionId : idx : fieldId — section + field ids are UUIDs, so a
    // well-formed key always splits into exactly four segments.
    if (parts.length !== 4) continue;
    const idx = Number.parseInt(parts[2] ?? '', 10);
    if (Number.isInteger(idx) && idx > maxIdx) maxIdx = idx;
  }
  return maxIdx + 1;
}

/** Read the current instance count for a repeatable section, clamped to the
 *  section's floor. Returns `min` (default 1) when the values map says nothing
 *  at all — new workflows lazily boot the first instance without a persisted
 *  value. Pass the section's real floor via {@link sectionMinInstances}; the
 *  default is only right for callers that genuinely have no section to consult.
 *  See {@link instanceCountFromValues} for how the unclamped count is derived. */
export function getInstanceCount(
  values: Record<string, FieldValue>,
  sectionId: string,
  min = 1,
): number {
  return Math.max(min, instanceCountFromValues(values, sectionId));
}

/** Write a new instance count. Returns a new values map — never mutates. */
export function setInstanceCount(
  values: Record<string, FieldValue>,
  sectionId: string,
  next: number,
): Record<string, FieldValue> {
  return { ...values, [instanceCountKey(sectionId)]: next };
}

/** Every section of every step, in document order — the lookup the
 *  "repeat once per instance of" helpers below take. */
export function allSectionsOf(template: {
  steps: ReadonlyArray<{ sections?: readonly FormSection[] | null }>;
}): FormSection[] {
  return template.steps.flatMap((s) => s.sections ?? []);
}

/**
 * The section whose instance count `section` mirrors (`repeatWithSectionId`),
 * or null when it mirrors nothing. A link that no longer resolves to another
 * live repeatable section is treated as no link at all, so a stale reference
 * degrades to an ordinary repeatable section instead of a frozen one.
 */
export function mirrorSourceOf(
  section: FormSection,
  allSections: readonly FormSection[],
): FormSection | null {
  const sourceId = section.repeatWithSectionId;
  if (!section.repeatable || !sourceId || sourceId === section.id) return null;
  const source = allSections.find((s) => s.id === sourceId);
  return source?.repeatable ? source : null;
}

/** The repeatable sections that mirror `section`'s instance count. */
export function mirrorsOf(
  section: FormSection,
  allSections: readonly FormSection[],
): FormSection[] {
  return allSections.filter(
    (s) => s.id !== section.id && mirrorSourceOf(s, allSections)?.id === section.id,
  );
}

/** A mirrored source plus every section mirroring it — instance k of each is
 *  "the same" holder. Always contains `section` itself. */
function instanceFamily(section: FormSection, allSections: readonly FormSection[]): FormSection[] {
  const root = mirrorSourceOf(section, allSections) ?? section;
  return [root, ...mirrorsOf(root, allSections)];
}

/** Remove one instance from a repeatable section, shifting all higher
 *  indices down by one. The last instance's slot is cleared. Returns a
 *  new values map — never mutates.
 *
 *  With `allSections`, every section that repeats once per instance of this
 *  one loses the same instance, so Accountholder 2's relationship answers go
 *  with Accountholder 2 instead of sliding onto whoever takes their slot. */
export function removeInstance(
  values: Record<string, FieldValue>,
  section: FormSection,
  removedIdx: number,
  allSections?: readonly FormSection[],
): Record<string, FieldValue> {
  let next = removeOwnInstance(values, section, removedIdx);
  if (next === values || !allSections) return next;
  for (const mirror of mirrorsOf(section, allSections)) {
    next = removeOwnInstance(next, mirror, removedIdx);
  }
  return next;
}

function removeOwnInstance(
  values: Record<string, FieldValue>,
  section: FormSection,
  removedIdx: number,
): Record<string, FieldValue> {
  const currentCount = getInstanceCount(values, section.id, sectionMinInstances(section));
  if (removedIdx < 0 || removedIdx >= currentCount) return values;
  const next: Record<string, FieldValue> = { ...values };
  const fieldIds = collectSectionFieldIds(section);
  for (let i = removedIdx; i < currentCount - 1; i++) {
    for (const fid of fieldIds) {
      const src = qualifiedKey(section.id, i + 1, fid);
      const dst = qualifiedKey(section.id, i, fid);
      const v = next[src];
      if (v === undefined) delete next[dst];
      else next[dst] = v;
    }
  }
  // Clear the last (now-orphan) instance's keys.
  for (const fid of fieldIds) {
    delete next[qualifiedKey(section.id, currentCount - 1, fid)];
  }
  next[instanceCountKey(section.id)] = currentCount - 1;
  return next;
}

/**
 * Project the global values map into ONE instance's frame of reference.
 *
 * Visibility conditions name their source by bare `field.id`. Inside a
 * repeatable section that id resolves to nothing, because every instance's
 * value lives under `inst:<sectionId>:<idx>:<fieldId>` — so a condition
 * whose source sits in the same repeatable would read `undefined` forever
 * and the reveal would never fire (or, for negative operators, never hide).
 *
 * This returns a copy where every field id authored INSIDE `section` is
 * rebound to the given instance's slot. Ids from anywhere else in the
 * workflow (a page-level radio above the repeatable, a prior step) fall
 * through untouched, so cross-section conditions keep working.
 *
 * Non-repeatable sections are returned as-is — the bare ids already are
 * the real keys, and skipping the copy keeps the hot render path free of
 * a per-row allocation.
 *
 * With `allSections`, the frame also covers the section's "repeat once per
 * instance of" family (`repeatWithSectionId`): the section it mirrors and
 * every section mirroring the same source bind to instance k as well. So a
 * question in Accountholder 2's relationship block can be gated on
 * Accountholder 2's own details, and a holder's fields — or a prefill rule
 * trying holder k — read that holder's relationship answers.
 */
export function scopeValuesToInstance(
  section: FormSection,
  values: Record<string, FieldValue>,
  instanceIdx: number,
  allSections?: readonly FormSection[],
): Record<string, FieldValue> {
  if (!section.repeatable) return values;
  const scoped: Record<string, FieldValue> = { ...values };
  const family = allSections ? instanceFamily(section, allSections) : [section];
  for (const member of family) {
    for (const fid of collectSectionFieldIds(member)) {
      scoped[fid] = values[qualifiedKey(member.id, instanceIdx, fid)];
    }
  }
  return scoped;
}

/** Walk the section's rows/columns and collect every field id it contains.
 *  Skips empty columns. Used by `removeInstance` to know which keys to
 *  shift/clear. */
function collectSectionFieldIds(section: FormSection): string[] {
  const out: string[] = [];
  for (const row of section.rows) {
    for (const col of row.columns) {
      if (col.field) out.push(col.field.id);
    }
  }
  return out;
}

/**
 * Iterate every (field, valueKey) pair inside a section. For non-
 * repeatable sections this yields the bare field id once per field. For
 * repeatable sections it yields the qualified key for every current
 * instance — so callers can walk uniformly without branching on
 * `section.repeatable` themselves.
 *
 * Used by the FormShell walkers (isStepComplete, missingRequiredFields,
 * source-prefill effects) that must see every value the advisor could
 * have entered anywhere in the section.
 */
export function forEachSectionField(
  section: FormSection,
  values: Record<string, FieldValue>,
  visit: (fieldId: string, valueKey: string, instanceIdx: number) => void,
): void {
  const fieldIds = collectSectionFieldIds(section);
  if (!section.repeatable) {
    for (const fid of fieldIds) visit(fid, fid, 0);
    return;
  }
  const count = getInstanceCount(values, section.id, sectionMinInstances(section));
  for (let i = 0; i < count; i++) {
    for (const fid of fieldIds) visit(fid, qualifiedKey(section.id, i, fid), i);
  }
}

/**
 * Resolve the "primary instance 0" value key for one specific field
 * inside a section. Used by lookups that only need one value per
 * authored field (per-option AcroForm booleans, sum-target lookups).
 * For non-repeatable sections this is the bare field id.
 */
export function valueKeyFor(section: FormSection, fieldId: string, instanceIdx = 0): string {
  return section.repeatable ? qualifiedKey(section.id, instanceIdx, fieldId) : fieldId;
}

/** Render the per-instance header — substitutes `{n}` (1-based) into the
 *  authored label pattern. Falls back to `"<title> {n}"` when no
 *  pattern is set. */
export function renderInstanceLabel(
  section: FormSection,
  instanceIdx: number,
  language: 'en' | 'fr',
): string {
  const raw =
    language === 'fr' && section.instanceLabelFr
      ? section.instanceLabelFr
      : (section.instanceLabelEn ?? '');
  const base =
    raw.trim().length > 0
      ? raw
      : `${(language === 'fr' ? section.titleFr : section.titleEn) || section.titleEn || section.titleFr} {n}`;
  return base.replace(/\{n\}/g, String(instanceIdx + 1));
}
